/**
 * POS Order Utilities - Helper functions for order processing calculations
 * Extracted to reduce POSPage.jsx complexity
 */

/**
 * Separate cart items by type
 * @param {Array} cart - Cart items
 * @returns {Object} - Categorized items
 */
export function categorizeCartItems(cart) {
  const coffeePassPurchases = cart.filter(item => item.isCoffeePass || item.is_coffee_pass);
  const coffeeClubRedemptions = cart.filter(item => 
    item.isCoffeeClubRedemption || item.isCoffeePassRedemption || 
    item.is_coffee_club_redemption || item.is_coffee_pass_redemption
  );
  const regularItems = cart.filter(item => 
    !item.isCoffeePass && !item.is_coffee_pass &&
    !item.isCoffeeClubRedemption && !item.isCoffeePassRedemption &&
    !item.is_coffee_club_redemption && !item.is_coffee_pass_redemption
  );
  
  return { coffeePassPurchases, coffeeClubRedemptions, regularItems };
}

/**
 * Calculate order totals with all discounts applied
 * @param {Array} cart - Cart items
 * @param {Object} customerDiscount - Customer loyalty discount { discount: number }
 * @param {Object} appliedCashierDiscount - Cashier applied discount { percent, amount, type }
 * @param {boolean} isOwnerOrder - Whether this is an owner order (free)
 * @param {number} eftposSurcharge - EFTPOS overseas card surcharge
 * @returns {Object} - Calculated totals
 */
export function calculateOrderTotals(cart, customerDiscount, appliedCashierDiscount, isOwnerOrder = false, eftposSurcharge = 0) {
  const { coffeePassPurchases, coffeeClubRedemptions, regularItems } = categorizeCartItems(cart);

  // Calculate discountable subtotal (regular items with per-item discounts)
  const discountableSubtotal = regularItems.reduce((sum, item) => {
    const itemTotal = item.price * item.quantity;
    let itemDiscount = 0;
    if (item.dollarDiscount > 0) {
      itemDiscount = item.dollarDiscount * item.quantity;
    } else if (item.discountPercent > 0) {
      itemDiscount = itemTotal * (item.discountPercent / 100);
    }
    return sum + (item.isComped ? 0 : (itemTotal - itemDiscount));
  }, 0);

  const coffeePassPurchaseSubtotal = coffeePassPurchases.reduce(
    (sum, item) => sum + (item.price * item.quantity), 0
  );
  const coffeeRedemptionSubtotal = coffeeClubRedemptions.reduce(
    (sum, item) => sum + (item.price * item.quantity), 0
  );

  // Calculate discounts
  const loyaltyDiscount = customerDiscount?.discount || 0;
  const cashierDiscountPercent = appliedCashierDiscount?.percent || 0;
  const cashierDiscountFixed = appliedCashierDiscount?.amount || 0;
  const isStaffDiscount = appliedCashierDiscount?.type === 'staff_50';
  const maxDiscount = isStaffDiscount ? 50 : 30;
  const totalDiscountPercent = Math.min(loyaltyDiscount + cashierDiscountPercent, maxDiscount);

  // Calculate cart-wide discount amount
  const percentageDiscountAmount = discountableSubtotal * (totalDiscountPercent / 100);
  const cartWideDiscountAmount = percentageDiscountAmount + cashierDiscountFixed;

  // Coffee Pass VAT is included in the price
  const coffeePassVAT = 0;

  // Calculate final total
  const baseTotal = discountableSubtotal - cartWideDiscountAmount + coffeePassPurchaseSubtotal + coffeeRedemptionSubtotal;
  const totalBeforeSurcharge = isOwnerOrder ? 0 : (baseTotal + coffeePassVAT);
  const total = totalBeforeSurcharge + (eftposSurcharge || 0);

  // Calculate VAT (12.5% inclusive)
  const taxRate = 0.125;
  const vatAmount = total * taxRate / (1 + taxRate);

  return {
    subtotal: discountableSubtotal + coffeePassPurchaseSubtotal + coffeeRedemptionSubtotal,
    discountableSubtotal,
    coffeePassPurchaseSubtotal,
    coffeeRedemptionSubtotal,
    discountAmount: cartWideDiscountAmount,
    discountPercent: totalDiscountPercent,
    loyaltyDiscount,
    cashierDiscountPercent,
    cashierDiscountFixed,
    vatAmount: Math.round(vatAmount * 100) / 100,
    eftposSurcharge: eftposSurcharge || 0,
    total: Math.round(total * 100) / 100,
    totalBeforeSurcharge: Math.round(totalBeforeSurcharge * 100) / 100,
    isOwnerOrder,
    hasCoffeePasses: coffeePassPurchases.length > 0,
    hasRedemptions: coffeeClubRedemptions.length > 0,
    coffeePassCount: coffeePassPurchases.reduce((sum, item) => sum + item.quantity, 0)
  };
}

/**
 * Build discount label for receipt printing
 * @param {Object} cashierDiscount - Applied cashier discount
 * @param {number} loyaltyDiscount - Loyalty percentage
 * @param {Object} customerDiscountInfo - Customer discount info
 * @returns {string|null} - Discount label
 */
export function buildDiscountLabel(cashierDiscount, loyaltyDiscount, customerDiscountInfo) {
  const labels = [];
  
  // Staff discount takes priority
  if (cashierDiscount?.type === 'staff_50') {
    return 'Staff 50%';
  }
  
  // Family & Friends discount
  if (cashierDiscount?.type === 'family_friends') {
    return 'Family & Friends 15%';
  }
  
  // Promo code
  if (cashierDiscount?.promo_code) {
    return `Promo: ${cashierDiscount.promo_code}`;
  }
  
  // Loyalty tier discount
  if (loyaltyDiscount > 0 && customerDiscountInfo?.tier) {
    labels.push(`Loyalty ${customerDiscountInfo.tier} ${loyaltyDiscount}%`);
  }
  
  // Additional cashier discount
  if (cashierDiscount?.percent > 0 && cashierDiscount?.type !== 'staff_50') {
    labels.push(`Discount ${cashierDiscount.percent}%`);
  }
  
  if (cashierDiscount?.amount > 0) {
    labels.push(`$${cashierDiscount.amount} off`);
  }
  
  return labels.length > 0 ? labels.join(' + ') : null;
}

/**
 * Format cart item for order submission
 * @param {Object} item - Cart item
 * @returns {Object} - Formatted item for API
 */
export function formatCartItemForOrder(item) {
  // Handle size - must be object or null, NOT a string
  let sizeObj = null;
  if (item.size && typeof item.size === 'object') {
    sizeObj = item.size;
  } else if (item.selectedSize && typeof item.selectedSize === 'object') {
    sizeObj = item.selectedSize;
  }
  
  // If size is a string, store in special_instructions
  const sizeStr = typeof item.size === 'string' ? item.size : 
                 (typeof item.selectedSize === 'string' ? item.selectedSize : null);

  const isCoffeePassItem = item.isCoffeePass || item.is_coffee_pass;
  const isCoffeePassRedemption = item.isCoffeeClubRedemption || item.isCoffeePassRedemption || 
                                  item.is_coffee_club_redemption || item.is_coffee_pass_redemption;

  // Fix redemption prices
  let itemPrice = item.price;
  if (isCoffeePassRedemption && itemPrice > 10) {
    console.warn(`Coffee pass redemption "${item.name}" has unexpected price $${itemPrice}, resetting to $0`);
    itemPrice = 0;
  }

  return {
    item_id: isCoffeePassItem 
      ? (item.item_id || item.product_id || 'bula-coffee-pass') 
      : (item.item_id || item.id || 'unknown').split('-')[0],
    name: item.name,
    price: itemPrice,
    quantity: item.quantity,
    category: item.category,
    customizations: item.customizations || [],
    size: sizeObj,
    selectedSize: sizeObj,
    modifiers: Array.isArray(item.modifiers) ? item.modifiers : null,
    special_instructions: sizeStr || item.special_instructions || item.specialNotes || null,
    add_ons: Array.isArray(item.add_ons) ? item.add_ons : (Array.isArray(item.addOns) ? item.addOns : null),
    removed_ingredients: Array.isArray(item.removed_ingredients) ? item.removed_ingredients : null,
    extra_ingredients: Array.isArray(item.extra_ingredients) ? item.extra_ingredients : null,
    is_coffee_club_redemption: isCoffeePassRedemption,
    is_coffee_pass: isCoffeePassItem,
    isCoffeePassRedemption: isCoffeePassRedemption,
    isCoffeeClubRedemption: isCoffeePassRedemption,
    passRedemptionId: item.passRedemptionId || null,
    plan_type: isCoffeePassItem ? (item.plan_type || 'standard') : undefined,
    product_id: isCoffeePassItem ? (item.product_id || item.item_id) : undefined,
    // Per-item discounts
    is_comped: item.isComped || false,
    discount_percent: item.discountPercent || 0,
    dollar_discount: item.dollarDiscount || 0,
    discount_by: item.discountBy || null
  };
}

/**
 * Determine final payment method from payment state
 * @param {string} paymentMethod - Selected payment method
 * @param {Array} splitPayments - Split payment entries
 * @returns {Object} - { method: string, isSplit: boolean }
 */
export function determinePaymentMethod(paymentMethod, splitPayments) {
  if (splitPayments && splitPayments.length > 0) {
    return { method: 'split', isSplit: true };
  }
  
  const method = paymentMethod === 'mpaisa_qr' ? 'mpaisa_qr' : paymentMethod;
  return { method, isSplit: false };
}

/**
 * Build complete order payload for API submission
 * @param {Object} params - Order parameters
 * @returns {Object} - Order payload for /api/pos/orders
 */
export function buildOrderPayload({
  cart,
  customer,
  customerDiscount,
  appliedCashierDiscount,
  paymentMethod,
  splitPayments,
  mpaisaRefCode,
  staff,
  orderType,
  tableNumber,
  isTrainingMode,
  isOwnerOrder,
  cashData,
  partnerCode,
  eftposSurcharge,
  getInvoiceType
}) {
  // Calculate totals
  const totals = calculateOrderTotals(cart, customerDiscount, appliedCashierDiscount, isOwnerOrder, eftposSurcharge);
  
  // Determine payment method
  const { method: finalPaymentMethod, isSplit: isSplitPayment } = determinePaymentMethod(paymentMethod, splitPayments);
  
  // Build discount label
  const discountLabel = buildDiscountLabel(appliedCashierDiscount, totals.loyaltyDiscount, customerDiscount);
  
  // For receipt: use total percent if only percentage, otherwise use combined percentage
  const totalDiscount = totals.cashierDiscountFixed > 0 && totals.cashierDiscountPercent === 0 
    ? 0 
    : totals.discountPercent;
  
  return {
    items: cart.map(formatCartItemForOrder),
    customer_id: customer?.contact_id || customer?.id,
    customer_email: customer?.email,
    customer_name: isOwnerOrder ? 'OWNER' : (customer?.name || customer?.first_name),
    subtotal: totals.subtotal,
    discount_percent: totalDiscount,
    discount_amount: totals.discountAmount,
    total: totals.total,
    payment_method: isOwnerOrder ? 'owner' : finalPaymentMethod,
    mpaisa_ref_code: mpaisaRefCode || null,
    staff_id: staff?.id,
    staff_name: staff?.name,
    order_type: orderType === 'dine-in' ? 'dine-in' : 'pos',
    table_number: orderType === 'dine-in' ? tableNumber : null,
    is_training: isTrainingMode,
    is_owner_order: isOwnerOrder,
    invoice_type: isOwnerOrder ? 'OWN' : (getInvoiceType ? getInvoiceType() : 'CS'),
    cash_received: cashData?.amountTendered || 0,
    change_given: cashData?.changeDue || 0,
    is_split_payment: isSplitPayment,
    split_payments: splitPayments || null,
    discount_type: appliedCashierDiscount?.type || (totals.loyaltyDiscount > 0 ? 'loyalty' : null),
    discount_label: discountLabel,
    staff_member_id: appliedCashierDiscount?.type === 'staff_50' ? appliedCashierDiscount.staffId : null,
    staff_member_name: appliedCashierDiscount?.type === 'staff_50' ? appliedCashierDiscount.staffName : null,
    partner_code: partnerCode || null,
    eftpos_surcharge: eftposSurcharge || 0,
    eftpos_card_type: eftposSurcharge > 0 ? 'overseas' : (paymentMethod === 'eftpos' ? 'local' : null),
    // Include calculated values for reference
    _calculated: {
      ...totals,
      isSplitPayment,
      finalPaymentMethod
    }
  };
}

/**
 * Extract error message from API error response
 * @param {Error} err - Error object from API call
 * @returns {string} - Human-readable error message
 */
export function extractErrorMessage(err) {
  if (err.response?.data?.detail) {
    return typeof err.response.data.detail === 'string' 
      ? err.response.data.detail 
      : JSON.stringify(err.response.data.detail);
  }
  if (err.response?.data?.message) {
    return err.response.data.message;
  }
  if (err.response?.data) {
    return typeof err.response.data === 'string'
      ? err.response.data
      : JSON.stringify(err.response.data);
  }
  if (err.message) {
    return err.message;
  }
  return 'Unknown error';
}

/**
 * Format cart item for kitchen/dine-in order
 * Used by sendToKitchen for item formatting
 * @param {Object} item - Cart item
 * @returns {Object} - Formatted item for kitchen API
 */
export function formatCartItemForKitchen(item) {
  return {
    item_id: item.item_id || item.id || 'unknown',
    name: item.name,
    price: item.price,
    quantity: item.quantity,
    category: item.category || 'general',
    special_instructions: item.special_instructions || item.specialNotes || null,
    size: item.size || item.selectedSize || null,
    selectedSize: item.selectedSize || item.size || null,
    modifiers: item.modifiers || null,
    add_ons: item.add_ons || item.addOns || null,
    addOns: item.addOns || item.add_ons || null,
    removed_ingredients: item.removed_ingredients || null,
    removedIngredients: item.removedIngredients || item.removed_ingredients || null,
    extra_ingredients: item.extra_ingredients || null,
    extraIngredients: item.extraIngredients || item.extra_ingredients || null,
    is_coffee_club_redemption: item.isCoffeeClubRedemption || item.isCoffeePassRedemption || 
                                item.is_coffee_club_redemption || item.is_coffee_pass || false
  };
}

/**
 * Build dine-in order payload for kitchen
 * @param {Object} params - Order parameters
 * @returns {Object} - Order payload for /api/pos/orders/send-to-kitchen
 */
export function buildKitchenOrderPayload({
  cart,
  customer,
  tableNumber,
  staff,
  isOwnerOrder,
  isTrainingMode
}) {
  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  return {
    items: cart.map(formatCartItemForKitchen),
    customer_id: customer?.contact_id || customer?.id || null,
    customer_name: isOwnerOrder ? 'OWNER' : (customer?.name || `Table ${tableNumber}`),
    customer_email: customer?.email || null,
    customer_phone: customer?.phone || null,
    subtotal: cartTotal,
    total: cartTotal,
    order_type: 'dine-in',
    table_number: String(tableNumber),
    payment_method: isOwnerOrder ? 'owner' : 'pending',
    payment_status: isOwnerOrder ? 'owner_comp' : 'pending',
    staff_id: staff?.id || null,
    staff_name: staff?.name || null,
    source: 'pos',
    is_owner_order: isOwnerOrder,
    is_training: isTrainingMode
  };
}

/**
 * Build add-items payload for existing order
 * @param {string} orderId - Existing order ID
 * @param {Array} items - Items to add
 * @returns {Object} - Payload for /api/pos/order/add-items
 */
export function buildAddItemsPayload(orderId, items) {
  return {
    order_id: orderId,
    items: items.map(formatCartItemForKitchen)
  };
}

export default {
  categorizeCartItems,
  calculateOrderTotals,
  buildDiscountLabel,
  formatCartItemForOrder,
  formatCartItemForKitchen,
  determinePaymentMethod,
  buildOrderPayload,
  buildKitchenOrderPayload,
  buildAddItemsPayload,
  extractErrorMessage
};

